let _scannedDevices = [];

const _listeners = [];

// Reference to ScratchBlocks workspace for refreshing toolbox
let _workspace = null;

/**
 * Set the ScratchBlocks workspace reference for toolbox refresh
 * @param {object} workspace - The ScratchBlocks workspace
 */
export const setWorkspace = (workspace) => {
    _workspace = workspace;
};

/**
 * Get the current workspace
 * @returns {object} The ScratchBlocks workspace
 */
export const getWorkspace = () => {
    return _workspace;
};

/**
 * Refresh the toolbox to update dynamic dropdowns
 * This forces blocks to re-render with new options
 */
export const refreshToolbox = () => {
    if (_workspace && typeof _workspace.refreshToolboxSelection_ === 'function') {
        try {
            _workspace.refreshToolboxSelection_();
            console.log('✓ Toolbox refreshed with new scanned devices');
        } catch (e) {
            console.warn('Could not refresh toolbox:', e);
        }
    }
    
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('scannedDevicesUpdated', { 
            detail: { devices: _scannedDevices } 
        }));
    }
};

/**
 * Set scanned devices
 * @param {Array} devices - Array of device objects from ESP32 scan
 * Each device object should have: { name: string, address: string } or similar structure
 */
export const setScannedDevices = (devices) => {
    _scannedDevices = Array.isArray(devices) ? devices : [];
    console.log('✓ Scanned devices saved to store:', _scannedDevices.length, 'devices');
    _listeners.forEach(listener => {
        try {
            listener(_scannedDevices);
        } catch (e) {
            console.error('Error notifying scanned devices listener:', e);
        }
    });
    
    refreshToolbox();
};

/**
 * Get scanned devices
 * @returns {Array} Array of scanned devices
 */
export const getScannedDevices = () => {
    return _scannedDevices;
};

/**
 * Add listener for device updates
 * @param {Function} listener - Callback function that receives updated devices array
 * @returns {Function} Unsubscribe function
 */
export const addScannedDevicesListener = (listener) => {
    if (typeof listener === 'function') {
        _listeners.push(listener);
    }
    return () => {
        const index = _listeners.indexOf(listener);
        if (index > -1) {
            _listeners.splice(index, 1);
        }
    };
};

export const clearScannedDevices = () => {
    _scannedDevices = [];
    console.log('✓ Scanned devices cleared');
    
    // Notify all listeners
    _listeners.forEach(listener => {
        try {
            listener(_scannedDevices);
        } catch (e) {
            console.error('Error notifying scanned devices listener:', e);
        }
    });
};

/**
 * Get scanned devices as dropdown options for Scratch Blocks
 * @param {Array} defaultOptions - Default options to use when no devices are scanned
 * @returns {Array} Array of [label, value] pairs for field_dropdown
 */
export const getScannedDevicesAsOptions = (defaultOptions = [['0', '0'], ['1', '1']]) => {
    if (_scannedDevices.length === 0) {
        return defaultOptions;
    }
    const filteredDevices = _scannedDevices.filter(device => device.found);
    if (filteredDevices.length === 0) {
        return defaultOptions;
    }
    
    return filteredDevices.map(device => {
        const label = String(device.port);
        const value = String(device.port);
        return [label, value];
    });
};

/**
 * Get scanned devices as dropdown options for Scratch Blocks (ADC type only)
 * @param {Array} defaultOptions - Default options to use when no devices are scanned
 * @returns {Array} Array of [label, value] pairs for field_dropdown
 */
export const getScannedDevices_adc = (defaultOptions = [['0', '0'], ['1', '1']]) => {
    if (_scannedDevices.length === 0) {
        return defaultOptions;
    }
    const filteredDevices = _scannedDevices.filter(device => device.found && device.commtype === "adc");
    if (filteredDevices.length === 0) {
        return defaultOptions;
    }
    
    return filteredDevices.map(device => {
        const label = String(device.port);
        const value = String(device.port);
        return [label, value];
    });
};

/**
 * Get scanned devices as dropdown options for Scratch Blocks (I2C type only)
 * @param {Array} defaultOptions - Default options to use when no devices are scanned
 * @returns {Array} Array of [label, value] pairs for field_dropdown
 */
export const getScannedDevices_i2c = (defaultOptions = [['0', '0'], ['1', '1']]) => {
    if (_scannedDevices.length === 0) {
        return defaultOptions;
    }
    const filteredDevices = _scannedDevices.filter(device => device.found && device.commtype === "i2c");
    if (filteredDevices.length === 0) {
        return defaultOptions;
    }
    
    return filteredDevices.map(device => {
        const label = String(device.port);
        const value = String(device.port);
        return [label, value];
    });
};

// RGB Sensor TCS3472
export const getScannedDevices_TCS3472 = (defaultOptions = [['0', '#define TCA_CHANNEL_TCS3472 1\n#define TCA_BUS_TCS3472 1'], ['1', '1']]) => {
    if (_scannedDevices.length === 0) {
        return defaultOptions;
    }
    const filteredDevices = _scannedDevices.filter(device => device.device_name ==="TCS3472" && device.commtype === "i2c");
    if (filteredDevices.length === 0) {
        return defaultOptions;
    }
    
    return filteredDevices.map(device => {
        const label = String(device.port);
        const channel = String(device.channel);
        const bus = String(device.bus);
        const value = `#define TCA_CHANNEL_TCS3472 ${channel}\n#define TCA_BUS_TCS3472 ${bus}\n`;
        return [label, value];
    });
};

// Distance Sensor VL6180X
export const getScannedDevices_VL6180X = (defaultOptions = [['0', '#define VL6180_SENSOR0_CHANNEL 1\n#define VL6180_SENSOR0_BUS 1\nVL6180X sensor0;\n'], ['1', '#define VL6180_SENSOR1_CHANNEL 1\n#define VL6180_SENSOR1_BUS 1\nVL6180X sensor1;\n']]) => {
    if (_scannedDevices.length === 0) {
        return defaultOptions;
    }
    const filteredDevices = _scannedDevices.filter(device => device.device_name ==="VL6180" && device.commtype === "i2c");
    if (filteredDevices.length === 0) {
        return defaultOptions;
    }
    
    return filteredDevices.map(device => {
        const label = String(device.port);  
        const channel = String(device.channel);
        const bus = String(device.bus);
        const port = String(device.port);
        const value = `#define VL6180_SENSOR${port}_CHANNEL ${channel}\n#define VL6180_SENSOR${port}_BUS ${bus}\nVL6180X sensor${port};\n`;
        return [label, value];
    });
};

// BME680 Sensor
export const getScannedDevices_BME680 = (defaultOptions = [['0', '#define BME680_BUS 0\n#define BME680_CHANNEL 3\n'], ['1', '#define BME680_BUS 0\n#define BME680_CHANNEL 3\n']]) => {
    if (_scannedDevices.length === 0) {
        return defaultOptions;
    }
    const filteredDevices = _scannedDevices.filter(device => device.device_name ==="BME680" && device.commtype === "i2c");
    if (filteredDevices.length === 0) {
        return defaultOptions;
    }
    
    return filteredDevices.map(device => {
        const label = String(device.port);  
        const channel = String(device.channel);
        const bus = String(device.bus);
        const value = `#define BME680_BUS ${bus}\n#define BME680_CHANNEL ${channel}\n#define SEALEVELPRESSURE_HPA (1013.25)\n`;
        return [label, value];
    });
};

// Oled Display SSD1306
export const getScannedDevices_OLED = (defaultOptions = [['0', '#define BME680_BUS 0\n#define BME680_CHANNEL 3\n'], ['1', '#define BME680_BUS 0\n#define BME680_CHANNEL 3\n']]) => {
    if (_scannedDevices.length === 0) {
        return defaultOptions;
    }
    const filteredDevices = _scannedDevices.filter(device => device.device_name ==="SSD1306" && device.commtype === "i2c");
    if (filteredDevices.length === 0) {
        return defaultOptions;
    }
    
    return filteredDevices.map(device => {
        const label = String(device.port);  
        const channel = String(device.channel);
        const bus = String(device.bus);
        const value = `#define OLED_BUS ${bus}\n#define OLED_CHANNEL ${channel}\n`;
        return [label, value];
    });
};

// HW484 Sensor
export const getScannedDevices_HW484 = (defaultOptions = [['0', '#define FLAME_HW484_A0 0\n'], ['1', '#define FLAME_HW484_A0 1\n']]) => {
    if (_scannedDevices.length === 0) {
        return defaultOptions;
    }
    const filteredDevices = _scannedDevices.filter(device => device.device_name ==="HW-484" && device.commtype === "A/D");
    if (filteredDevices.length === 0) {
        return defaultOptions;
    }
    
    return filteredDevices.map(device => {
        const label = String(device.port);  
        const analog = String(device.analog);
        const value = `#define FLAME_HW484_A0 ${analog}`;
        return [label, value];
    });
};